import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { XCircle, CreditCard } from 'lucide-react';
import ManagementPage from '../../Common/components/common/ManagementPage';
import DetailItem from '../../Common/components/common/DetailItem';
import DetailGrid from '../../Common/components/common/DetailGrid';
import { useToast } from '../../Common/context/ToastContext';
import { downloadBlob } from '../../Common/api/client';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const RECEIPT_SMS_ENABLED = Number(process.env.REACT_APP_RECEIPT_SMS_ENABLED ?? 0) === 1;
const RECEIPT_KAKAO_ENABLED = Number(process.env.REACT_APP_RECEIPT_KAKAO_ENABLED ?? 0) === 1;

// UTC → 한국시간(KST, UTC+9) 포맷
function formatKST(dateStr, format = 'YYYY.MM.DD HH:mm') {
  if (!dateStr) return '-';
  return dayjs.utc(dateStr).add(9, 'hour').format(format);
}

// AppPaymentList.js와 동일한 카드명 파싱
function parseCardNameFromRaw(pgRawResponse) {
  if (!pgRawResponse || typeof pgRawResponse !== 'string') return '-';
  try {
    const parsed = JSON.parse(pgRawResponse);
    const data = parsed?.data;
    return data?.cardNm || data?.cardKind || '-';
  } catch (_) {
    return '-';
  }
}

// API 응답 → 화면에서 쓰기 좋은 트랜잭션 형태로 변환
function mapPaymentToTransaction(item) {
  const paymentTransactionId =
    item.payment_transaction_id ??
    item.paymentTransactionId ??
    item.id ??
    item.paymentId ??
    item.payment_id;
  const id =
    item.transaction_id ??
    item.transactionId ??
    [
      paymentTransactionId ?? 'payment',
      item.transaction_type ?? item.transactionType ?? 'UNKNOWN',
      item.business_created_at ?? item.businessCreatedAt ?? '',
    ].join(':');

  const businessDate =
    item.business_created_at ??
    item.businessCreatedAt;
  const dateStr =
    businessDate ??
    item.approved_at ??
    item.approvedAt ??
    item.approval_date ??
    item.approvalDate ??
    item.created_at ??
    item.createdAt ??
    item.date;
  const date = dateStr
    ? businessDate
      ? dayjs.utc(dateStr).subtract(9, 'hour').toISOString()
      : dayjs.utc(dateStr).toISOString()
    : dayjs().toISOString();

  const instmtMon =
    item.instmt_mon ??
    item.instmtMon ??
    item.pg_instmt_mon ??
    item.installmentMonths ??
    item.installment_months ??
    '00';
  const type = instmtMon === '00' ? '일시불' : `${instmtMon}개월`;

  const cardName =
    item.card_nm ??
    item.cardNm ??
    item.cardName ??
    parseCardNameFromRaw(item.pg_raw_response) ??
    '-';

  const status = item.transaction_status ?? item.transactionStatus ?? item.status ?? '';
  const transactionType = String(
    item.transaction_type ?? item.transactionType ?? ''
  ).toUpperCase();
  const amount = Number(
    item.amount ??
      item.trdAmt ??
      item.trd_amt ??
      item.pg_trd_amt ??
      item.request_trd_amt ??
      0
  );

  return {
    id,
    paymentTransactionId,
    paymentId: paymentTransactionId,
    date,
    pharmacy:
      item.transaction_pharmacy_name ??
      item.transactionPharmacyName ??
      item.mcht_cust_nm ??
      item.mchtCustNm ??
      item.pharmacy ??
      item.customer_name ??
      item.pharmacy_name ??
      item.customerName ??
      item.pharmacyName ??
      '-',
    amount: transactionType === 'CANCEL' ? -Math.abs(amount) : amount,
    approvalNo:
      item.appr_no ??
      item.apprNo ??
      item.approvalNo ??
      item.approval_no ??
      item.approvalNumber ??
      '',
    fee: Number(
      item.cp_fee_amount ??
        item.cpFeeAmount ??
        item.fee_amt ??
        item.feeAmt ??
        item.fee_amount ??
        item.fee ??
        0
    ),
    // API의 salesperson_name 필드를 우선 사용
    driver:
      item.salesperson_name ??
      item.salespersonName ??
      item.salesperson ??
      item.driver ??
      item.driver_name ??
      item.driverName ??
      '',
    type,
    cancelStatus:
      transactionType === 'CANCEL' ||
      String(status).toUpperCase() === 'CANCELED' ||
      item.canceled_at != null,
    status,
    transactionType,
    cardName,
  };
}

function getStatusLabelFromTx(tx) {
  if (tx.cancelStatus) return '취소';
  return '승인';
}

function PaymentList() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({ totalCount: 0, totalAmount: 0 });
  const [isExcelDownloading, setIsExcelDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const loadingMoreRef = useRef(false);
  const infiniteScrollRef = useRef(null);
  const activeRequestRef = useRef(0);
  const pageRef = useRef(page);
  const hasNextRef = useRef(hasNext);
  pageRef.current = page;
  hasNextRef.current = hasNext;
  const [selectedItem, setSelectedItem] = useState(null);
  // 기본 기간: 당월 1일 ~ 오늘 (정산달력에서 넘어온 경우 해당 날짜 사용)
  const getDefaultMonthRange = () => {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const formatDate = (d) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    return {
      start: formatDate(monthStart),
      end: formatDate(today),
    };
  };
  const defaultRange = getDefaultMonthRange();
  const initialDate = location.state?.searchDate ?? defaultRange.end;
  const [startDate, setStartDate] = useState(location.state?.searchDate ? initialDate : defaultRange.start);
  const [endDate, setEndDate] = useState(initialDate);
  const [searchInput, setSearchInput] = useState(''); // 검색창 입력값
  const [searchQuery, setSearchQuery] = useState(''); // API q 파라미터 (검색 버튼/Enter 시 적용)

  // 정산달력에서 "전체내역"으로 올 때 전달된 날짜 적용 후 state 제거(URL 유지)
  useEffect(() => {
    if (location.state?.searchDate) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [isSendingReceipt, setIsSendingReceipt] = useState(false);
  const [sendMethod, setSendMethod] = useState('email'); // 'email' | 'sms' | 'kakao'
  const [receiptPhone, setReceiptPhone] = useState('');

  const setQuickPeriod = (type) => {
    const today = new Date();
    const formatDate = (d) =>
      `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(
        d.getDate()
      ).padStart(2, '0')}`;
    const parseDate = (s) => {
      const [y, m, d] = s.split('.').map(Number);
      return new Date(y, m - 1, d);
    };
    let currentStart = parseDate(startDate);
    let currentEnd = parseDate(endDate);
    let newStart, newEnd;
    switch (type) {
      case '당일':
        newStart = newEnd = today;
        break;
      case '전일':
        // 현재 조회 구간의 "마지막날(endDate)" 기준으로 전일로 이동
        // 예) 2/1~2/9 상태에서 '전일' 클릭 시 2/8 하루로 이동
        newEnd = new Date(currentEnd);
        newEnd.setDate(currentEnd.getDate() - 1);
        newStart = new Date(newEnd);
        break;
      case '당월':
        newStart = new Date(today.getFullYear(), today.getMonth(), 1);
        newEnd = today;
        break;
      case '전월':
        newStart = new Date(currentStart.getFullYear(), currentStart.getMonth() - 1, 1);
        newEnd = new Date(currentStart.getFullYear(), currentStart.getMonth(), 0);
        break;
      default:
        return;
    }
    setStartDate(formatDate(newStart));
    setEndDate(formatDate(newEnd));
  };

  const fetchTransactions = useCallback(async (pageNum = 1, append = false) => {
    if (append && loadingMoreRef.current) return;
    const requestId = ++activeRequestRef.current;
    if (append) {
      loadingMoreRef.current = true;
    } else {
      setLoading(true);
      setHasNext(false);
    }
    const baseUrl = process.env.REACT_APP_API || '';
    const dateFrom = startDate.replace(/\./g, '-');
    const dateTo = endDate.replace(/\./g, '-');
    try {
      // GET /api/cp/payment/manual - page, limit, status, dateFrom, dateTo, q(약국명 검색)
      const params = {
        page: pageNum,
        limit: 100,
        dateFrom,
        dateTo,
      };
      if (searchQuery.trim()) params.q = searchQuery.trim();

      const { data } = await axios.get(baseUrl + '/api/cp/payment/manual', {
        params,
        withCredentials: true,
      });

      if (requestId !== activeRequestRef.current) return;
      if (data?.code !== 1000) {
        if (!append) {
          setTransactions([]);
          setSummary({ totalCount: 0, totalAmount: 0 });
        }
        return;
      }

      const responseData = data.data || {};
      const rows = Array.isArray(responseData.rows)
        ? responseData.rows
        : Array.isArray(responseData)
          ? responseData
          : [];
      const filteredRows = rows.filter((item) => {
        const status = String(
          item.transaction_status ?? item.transactionStatus ?? item.status ?? ''
        ).toUpperCase();
        return status !== 'FAILED';
      });
      const mappedRows = filteredRows.map(mapPaymentToTransaction);
      const apiSummary = responseData.summary || {};
      const totalCount = Number(
        apiSummary.transactionCount ??
        apiSummary.transaction_count ??
        responseData.total ??
        mappedRows.length
      );
      const approvalAmount = Number(
        apiSummary.approvalAmount ??
        apiSummary.approval_amount ??
        apiSummary.completedAmount ??
        0
      );
      const cancelAmount = Number(
        apiSummary.cancelAmount ??
        apiSummary.cancel_amount ??
        apiSummary.canceledAmount ??
        0
      );
      const netPaymentAmount = apiSummary.netPaymentAmount ?? apiSummary.net_payment_amount;
      const totalAmount = netPaymentAmount != null
        ? Number(netPaymentAmount)
        : approvalAmount || cancelAmount
          ? approvalAmount - Math.abs(cancelAmount)
          : mappedRows.reduce((sum, item) => sum + Number(item.amount || 0), 0);
      const limit = Number(responseData.limit ?? params.limit);
      const nextHasNext = responseData.hasNext !== undefined
        ? Boolean(responseData.hasNext)
        : pageNum * limit < Number(responseData.total ?? mappedRows.length);

      if (append) {
        setTransactions((current) => {
          const merged = new Map(current.map((item) => [String(item.id), item]));
          mappedRows.forEach((item) => merged.set(String(item.id), item));
          return Array.from(merged.values());
        });
      } else {
        setTransactions(mappedRows);
      }
      setSummary({ totalCount, totalAmount });
      setPage(pageNum);
      setHasNext(nextHasNext);
    } catch {
      if (requestId !== activeRequestRef.current) return;
      if (!append) {
        setTransactions([]);
        setSummary({ totalCount: 0, totalAmount: 0 });
        setPage(1);
        setHasNext(false);
      }
    } finally {
      if (requestId === activeRequestRef.current) {
        setLoading(false);
        loadingMoreRef.current = false;
      }
    }
  }, [startDate, endDate, searchQuery]);

  useEffect(() => {
    fetchTransactions(1, false);
  }, [fetchTransactions]);

  useEffect(() => {
    let observer = null;
    const frameId = requestAnimationFrame(() => {
      const target = infiniteScrollRef.current;
      if (!target) return;
      observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry?.isIntersecting || loadingMoreRef.current || !hasNextRef.current) return;
          fetchTransactions(pageRef.current + 1, true);
        },
        { rootMargin: '100px', threshold: 0 }
      );
      observer.observe(target);
    });
    return () => {
      cancelAnimationFrame(frameId);
      if (observer) observer.disconnect();
    };
  }, [fetchTransactions, transactions.length]);

  const totalCount = summary.totalCount;
  const totalAmount = summary.totalAmount;

  const handleExcelDownload = useCallback(async () => {
    if (isExcelDownloading) return;
    if (totalCount <= 0) {
      showToast('다운로드할 결제내역이 없습니다.', 'error');
      return;
    }

    const dateFrom = startDate.replace(/\./g, '-');
    const dateTo = endDate.replace(/\./g, '-');
    const params = { dateFrom, dateTo };
    if (searchQuery.trim()) params.q = searchQuery.trim();

    setIsExcelDownloading(true);
    try {
      await downloadBlob({
        url: '/api/cp/payment/manual/export',
        params,
        fallbackFilename: `결제내역_${dateFrom}_${dateTo}.xlsx`,
      });
      showToast('엑셀 다운로드가 시작되었습니다.');
    } catch (error) {
      showToast(error?.message || '엑셀 다운로드에 실패했습니다.', 'error');
    } finally {
      setIsExcelDownloading(false);
    }
  }, [endDate, isExcelDownloading, searchQuery, showToast, startDate, totalCount]);

  const handleConfirmCancel = async () => {
    if (!selectedItem) return;
    const paymentTransactionId = selectedItem.paymentTransactionId;
    if (paymentTransactionId == null) {
      showToast('취소할 결제 ID가 없습니다.', 'error');
      return;
    }

    setCancelModalOpen(false);

    const baseUrl = process.env.REACT_APP_API || '';
    try {
      const { data } = await axios.post(
        baseUrl + '/api/cp/payment/manual/cancel',
        {
          paymentTransactionId,
          reason: '가맹점 취소 요청',
        },
        { withCredentials: true }
      );

      const status = data?.data?.status;
      const providerResult = data?.data?.provider_result || {};
      const success = providerResult?.success !== false && status === 'COMPLETED';

      if (data?.code === 1000 && success) {
        showToast(data?.msg || '결제취소가 완료되었습니다.');
        // 목록 전체를 다시 불러와서 테이블/상세 정보를 동기화
        fetchTransactions();
        // 이미 열려 있던 상세정보에도 취소 상태를 반영해서 취소 버튼이 사라지도록 처리
        setSelectedItem((prev) => {
          if (!prev) return prev;
          if (prev.paymentTransactionId !== paymentTransactionId) return prev;
          return {
            ...prev,
            cancelStatus: true,
            status: 'CANCELED',
          };
        });
      } else {
        const msg =
          data?.data?.outRsltMsg ?? data?.data?.provider_result?.outRsltMsg ?? data?.msg ?? '결제취소에 실패했습니다.';
        showToast(msg, 'error');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.msg || '결제취소에 실패했습니다.';
      showToast(msg, 'error');
    }
  };

  const handleConfirmSendReceipt = async () => {
    if (!selectedItem) return;
    const paymentTransactionId = selectedItem.paymentTransactionId;
    if (paymentTransactionId == null) {
      showToast('전표 ID가 없습니다.', 'error');
      return;
    }

    const closeReceipt = () => {
      setReceiptModalOpen(false);
      setReceiptEmail('');
      setReceiptPhone('');
      setSendMethod('email');
    };

    const baseUrl = process.env.REACT_APP_API || '';

    if (sendMethod === 'email') {
      const email = (receiptEmail || '').trim();
      if (!email) {
        showToast('전송할 이메일을 입력해주세요.', 'error');
        return;
      }
      try {
        setIsSendingReceipt(true);
        const { data } = await axios.post(
          baseUrl + '/api/cp/payment/manual/receipt-email',
          { paymentTransactionId, payment_transaction_id: paymentTransactionId, email },
          { withCredentials: true }
        );
        if (data?.code === 1000) {
          showToast(data?.msg || '이메일로 전표가 전송되었습니다.');
          closeReceipt();
        } else {
          showToast(data?.msg || '전표 이메일 전송에 실패했습니다.', 'error');
        }
      } catch (err) {
        showToast(err?.response?.data?.msg || '전표 이메일 전송 중 오류가 발생했습니다.', 'error');
      } finally {
        setIsSendingReceipt(false);
      }
    } else if (sendMethod === 'sms') {
      const phone = (receiptPhone || '').replace(/[^0-9]/g, '');
      if (!phone) {
        showToast('휴대폰 번호를 입력해주세요.', 'error');
        return;
      }
      try {
        setIsSendingReceipt(true);
        const { data } = await axios.post(
          baseUrl + '/api/cp/payment/manual/receipt-sms',
          { paymentTransactionId, phone },
          { withCredentials: true }
        );
        if (data?.code === 1000) {
          showToast(data?.msg || '전표 문자가 전송되었습니다.');
          closeReceipt();
        } else {
          showToast('관리자에게 문의해주세요.', 'error');
        }
      } catch (err) {
        showToast('관리자에게 문의해주세요.', 'error');
      } finally {
        setIsSendingReceipt(false);
      }
    } else if (sendMethod === 'kakao') {
      const phone = (receiptPhone || '').replace(/[^0-9]/g, '');
      if (!phone) {
        showToast('휴대폰 번호를 입력해주세요.', 'error');
        return;
      }
      try {
        setIsSendingReceipt(true);
        const { data } = await axios.post(
          baseUrl + '/api/cp/payment/manual/receipt-alimtalk',
          { paymentTransactionId, phone },
          { withCredentials: true }
        );
        if (data?.code === 1000) {
          showToast(data?.msg || '전표 알림톡을 발송했습니다.');
          closeReceipt();
        } else {
          showToast(data?.msg || '전표 알림톡 발송에 실패했습니다.', 'error');
        }
      } catch (err) {
        showToast(err?.response?.data?.msg || '전표 알림톡 발송 중 오류가 발생했습니다.', 'error');
      } finally {
        setIsSendingReceipt(false);
      }
    }
  };

  return (
    <>
      <ManagementPage
        title="결제내역"
        emptyStateIcon={<CreditCard size={32} className="opacity-20" />}
        hideSearch={true}
        filters={
          <div className="flex items-center gap-3 flex-wrap">
            <div className="h-[42px] flex items-center gap-2 bg-[#f2f4f6] px-3 rounded-lg border border-transparent focus-within:border-[var(--brand-primary)] transition-all shrink-0 w-[250px]">
              <input
                type="text"
                placeholder="약국명 검색"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearchQuery(searchInput.trim());
                  }
                }}
                className="bg-transparent border-none outline-none text-[14px] text-[#191f28] py-0 h-full flex-1 min-w-0"
              />
              <button
                type="button"
                onClick={() => setSearchQuery(searchInput.trim())}
                className="px-2 py-1 rounded bg-[var(--brand-primary)] text-white text-[12px] font-bold hover:opacity-90 shrink-0"
              >
                검색
              </button>
            </div>
            <div className="h-4 w-[1px] bg-[#e5e8eb]" />
            <div className="h-[42px] flex items-center gap-1.5 bg-[#f2f4f6] px-3 rounded-lg border border-transparent focus-within:border-[var(--brand-primary)] transition-all">
              <input
                type="date"
                value={startDate.replace(/\./g, '-')}
                onChange={(e) =>
                  setStartDate(e.target.value.replace(/-/g, '.'))
                }
                className="bg-transparent border-none outline-none text-[14px] font-mono text-[#191f28] py-0 h-full"
              />
              <span className="text-[#8b95a1] text-[12px]">~</span>
              <input
                type="date"
                value={endDate.replace(/\./g, '-')}
                onChange={(e) =>
                  setEndDate(e.target.value.replace(/-/g, '.'))
                }
                className="bg-transparent border-none outline-none text-[14px] font-mono text-[#191f28] py-0 h-full"
              />
            </div>
            <div className="flex gap-1">
              {['당일', '전일', '당월', '전월'].map((type) => (
                <button
                  key={type}
                  onClick={() => setQuickPeriod(type)}
                  className="px-2.5 py-1.5 bg-white border border-[#e5e8eb] rounded-lg text-[12px] font-bold text-[#4e5968] hover:bg-[#f2f4f6] leading-none h-[42px] flex items-center"
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        }
        leftActions={
          <div className="text-[16px] font-bold text-[#191f28]">
            총 결제 금액:{' '}
            <span className="text-[var(--brand-primary)]">
              {totalAmount.toLocaleString()}원
            </span>{' '}
            ({totalCount}건)
          </div>
        }
        onExcelDownload={handleExcelDownload}
        excelButtonClassName="inline-flex items-center gap-2 px-3 py-1.5 bg-[#191f28] text-white rounded-lg font-bold text-[14px] hover:bg-black transition-colors"
        excelButtonDisabled={isExcelDownloading}
        excelButtonLabel={isExcelDownloading ? '다운로드 중...' : '엑셀다운로드'}
        tableHeaders={
          <>
            <th className="px-4 py-3">결제일</th>
            <th className="px-4 py-3">약국명</th>
            <th className="px-4 py-3">상태</th>
            <th className="px-4 py-3 text-right">금액</th>
            <th className="px-4 py-3 text-right">수수료</th>
            <th className="px-4 py-3">영업자</th>
          </>
        }
        data={transactions}
        infiniteScrollRef={infiniteScrollRef}
        renderRow={(item) => {
          const isCanceled = item.cancelStatus;
          const rowBg = isCanceled ? ' bg-[#fef2f2]' : '';
          return (
            <>
              <td className={`px-4 py-3 text-[#4e5968] whitespace-nowrap${rowBg}`}>
                {formatKST(item.date, 'YYYY.MM.DD HH:mm')}
              </td>
              <td className={`px-4 py-3 font-bold text-[#191f28]${rowBg}`}>
                {item.pharmacy}
              </td>
              <td className={`px-4 py-3 text-[#4e5968]${rowBg}`}>
                {getStatusLabelFromTx(item)}
              </td>
              <td className={`px-4 py-3 text-right text-[#191f28]${rowBg}`}>
                {Number(item.amount || 0).toLocaleString()}원
              </td>
              <td className={`px-4 py-3 text-right text-[#4e5968]${rowBg}`}>
                {Number(item.fee ?? 0).toLocaleString()}원
              </td>
              <td className={`px-4 py-3 text-[#4e5968]${rowBg}`}>{item.driver || '-'}</td>
            </>
          );
        }}
        detailTitle="결제 상세정보"
        selectedItem={selectedItem}
        onSelectItem={setSelectedItem}
        detailButtonLabel={selectedItem ? 'email 전표전송' : undefined}
        detailButtonIcon={selectedItem ? <CreditCard size={14} /> : undefined}
        detailButtonSmall
        detailButtonDisabled={!selectedItem}
        onDetailButtonClick={() => {
          if (!selectedItem) return;
          setReceiptModalOpen(true);
        }}
        renderDetail={(item) => (
          <>
            {/* 상세 상단 오른쪽: 취소 버튼만 (승인 건에서만 표시) */}
            <div className="mb-3 flex justify-end">
              {!item.cancelStatus && (
                <button
                  type="button"
                  onClick={() => setCancelModalOpen(true)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#fecaca] text-[13px] font-semibold text-[#b91c1c] bg-[#fef2f2] hover:bg-[#fee2e2]"
                >
                  <XCircle size={14} />
                  <span>취소</span>
                </button>
              )}
            </div>
            <DetailGrid>
              <DetailItem
                label="결제일"
                value={formatKST(item.date, 'YYYY.MM.DD HH:mm')}
              />
              <DetailItem label="약국명" value={item.pharmacy} />
              <DetailItem
                label="금액"
                value={`${Number(item.amount || 0).toLocaleString()}원`}
                color="#191f28"
              />
              <DetailItem
                label="수수료"
                value={`${Number(item.fee ?? 0).toLocaleString()}원`}
              />
              <DetailItem label="상태" value={getStatusLabelFromTx(item)} />
              <DetailItem label="할부" value={item.type} />
              <DetailItem label="영업자" value={item.driver || '-'} />
              <DetailItem label="승인번호" value={item.approvalNo || '-'} />
            </DetailGrid>
          </>
        )}
      />

      {/* 취소 확인 모달 */}
      {cancelModalOpen && selectedItem && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000]">
          <div className="bg-white rounded-2xl px-6 py-5 w-full max-w-[360px] shadow-xl">
            <div className="text-[18px] font-bold text-[#111827] mb-2">
              결제를 취소하시겠습니까?
            </div>
            <div className="text-[14px] text-[#6b7280] mb-5 leading-relaxed">
              선택한 거래(
              <span className="font-semibold">{selectedItem.pharmacy}</span>,{' '}
              <span className="font-semibold">
                {Number(selectedItem.amount || 0).toLocaleString()}원
              </span>
              )를 취소 처리합니다.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCancelModalOpen(false)}
                className="flex-1 px-3 py-2.5 rounded-xl text-[14px] font-semibold bg-[#e5e7eb] text-[#111827]"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={handleConfirmCancel}
                className="flex-1 px-3 py-2.5 rounded-xl text-[14px] font-semibold bg-[#ef4444] text-white"
              >
                취소하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 전표전송 모달 */}
      {receiptModalOpen && selectedItem && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000]" onClick={() => { if (isSendingReceipt) return; setReceiptModalOpen(false); setReceiptEmail(''); setReceiptPhone(''); setSendMethod('email'); }}>
          <div className="bg-white rounded-2xl px-6 py-5 w-full max-w-[360px] shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-[18px] font-bold text-[#111827] mb-4">
              전표 전송
            </div>
            {/* 전송 방법 선택 */}
            <div className="flex rounded-lg overflow-hidden border border-[#e5e7eb] mb-3">
              <button type="button" onClick={() => setSendMethod('email')} className={`flex-1 py-2 text-[13px] font-semibold ${sendMethod === 'email' ? 'bg-[var(--brand-primary)] text-white' : 'bg-white text-[#4e5968] hover:bg-[#f2f4f6]'}`}>이메일</button>
              <button type="button" onClick={() => { if (RECEIPT_SMS_ENABLED) setSendMethod('sms'); }} className={`flex-1 py-2 text-[13px] font-semibold border-x border-[#e5e7eb] ${!RECEIPT_SMS_ENABLED ? 'cursor-not-allowed text-[#adb5bd]' : sendMethod === 'sms' ? 'bg-[#00b493] text-white' : 'bg-white text-[#4e5968] hover:bg-[#f2f4f6]'}`}>{RECEIPT_SMS_ENABLED ? '문자' : '문자(준비중)'}</button>
              <button type="button" onClick={() => { if (RECEIPT_KAKAO_ENABLED) setSendMethod('kakao'); }} className={`flex-1 py-2 text-[13px] font-semibold ${!RECEIPT_KAKAO_ENABLED ? 'cursor-not-allowed text-[#adb5bd]' : sendMethod === 'kakao' ? 'bg-[#fee500] text-[#3c1e1e]' : 'bg-white text-[#4e5968] hover:bg-[#f2f4f6]'}`}>{RECEIPT_KAKAO_ENABLED ? '카카오톡' : '카카오톡(준비중)'}</button>
            </div>
            {sendMethod === 'email' && (
              <>
                <label className="block text-[13px] font-semibold text-[#4e5968] mb-1.5">이메일 주소</label>
                <input
                  type="email"
                  value={receiptEmail}
                  onChange={(e) => setReceiptEmail(e.target.value)}
                  placeholder="example@email.com"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !isSendingReceipt) handleConfirmSendReceipt(); }}
                  className="w-full mb-3 px-3 py-2.5 rounded-xl border border-[#d1d5db] text-[14px] outline-none focus:border-[var(--brand-primary)]"
                />
              </>
            )}
            {sendMethod === 'sms' && (
              <>
                <label className="block text-[13px] font-semibold text-[#4e5968] mb-1.5">휴대폰 번호</label>
                <input
                  type="tel"
                  value={receiptPhone}
                  onChange={(e) => setReceiptPhone(e.target.value)}
                  placeholder="01012345678"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !isSendingReceipt) handleConfirmSendReceipt(); }}
                  className="w-full mb-3 px-3 py-2.5 rounded-xl border border-[#d1d5db] text-[14px] outline-none focus:border-[var(--brand-primary)]"
                />
              </>
            )}
            {sendMethod === 'kakao' && (
              <>
                <label className="block text-[13px] font-semibold text-[#4e5968] mb-1.5">휴대폰 번호</label>
                <input type="tel" value={receiptPhone} onChange={(e) => setReceiptPhone(e.target.value)} placeholder="01012345678" onKeyDown={(e) => { if (e.key === 'Enter' && !isSendingReceipt) handleConfirmSendReceipt(); }} className="w-full mb-3 px-3 py-2.5 rounded-xl border border-[#d1d5db] text-[14px] outline-none focus:border-[var(--brand-primary)]" />
              </>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  if (isSendingReceipt) return;
                  setReceiptModalOpen(false);
                  setReceiptEmail('');
                  setReceiptPhone('');
                  setSendMethod('email');
                }}
                className="flex-1 px-3 py-2.5 rounded-xl text-[14px] font-semibold bg-[#e5e7eb] text-[#111827]"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={handleConfirmSendReceipt}
                disabled={isSendingReceipt || (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED)}
                className={`flex-1 px-3 py-2.5 rounded-xl text-[14px] font-semibold ${(sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? 'bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed' : sendMethod === 'kakao' ? 'bg-[#fee500] text-[#3c1e1e] hover:bg-[#f0d900]' : isSendingReceipt ? 'bg-[var(--brand-primary-disabled)] text-white cursor-not-allowed' : sendMethod === 'sms' ? 'bg-[#00b493] text-white' : 'bg-[var(--brand-primary)] text-white'}`}
              >
                {isSendingReceipt ? '전송중...' : (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? '준비중' : sendMethod === 'kakao' ? '알림톡 전송' : sendMethod === 'sms' ? '문자 전송' : '이메일 전송'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PaymentList;
